import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {connect} from 'react-redux';
import addAdyenV2OptionsToState from '../../v2/actions/paymentActions/adyenV2/addAdyenV2OptionsToState';
import getSessionDataForAdyenV2 from '../../v2/actions/paymentActions/adyenV2/getSessionDataForAdyenV2';
import sendAdyenV2ActionResponse from '../../v2/actions/paymentActions/adyenV2/sendAdyenV2ActionResponse';
import setAdyenV2Checkout from '../../v2/actions/paymentActions/adyenV2/setAdyenV2Checkout';
import setAdyenV2PlaceOrderData from '../../v2/actions/paymentActions/adyenV2/setAdyenV2PlaceOrderData';
import initialiseBillingAddressForPaymentType
from '../../v2/actions/paymentActions/billingAddressActions/initialiseBillingAddressForPaymentType';
import setPaymentOptionsLoadingStatus from '../../v2/actions/paymentActions/setPaymentOptionsLoadingStatus';
import getAdyenV2Checkout from '../../v2/selectors/PaymentOptionSelectors/AdyenV2/getAdyenV2Checkout';
import getAdyenV2PaymentMethodsConfiguration
from '../../v2/selectors/PaymentOptionSelectors/AdyenV2/getAdyenV2PaymentMethodsConfiguration';
import getAdyenV2SessionData from '../../v2/selectors/PaymentOptionSelectors/AdyenV2/getAdyenV2SessionData';
import getBlockedSubTypesForAdyenV2
from '../../v2/selectors/PaymentOptionSelectors/AdyenV2/getBlockedSubTypesForAdyenV2';
import {getPaymentOptions} from '../../v2/selectors/PaymentOptionSelectors/PaymentOptionSelectors';
import {getLocaleFromSiteDataV2, getSettings} from '../../v2/selectors/selectors';
import useAdyenV2LoadedHandler from './useAdyenV2LoadedHandler';
import useAdyenV2SessionLoader from './useAdyenV2SessionLoader';

export function AdyenV2OptionsLoader(props) {
  const handleAdyenV2CheckoutInitialisation = adyenCheckout => props.setAdyenV2Checkout(adyenCheckout);
  
  useEffect(() => {
    // If session data already exits dont recall, this saves calls to API.
    // However, there may be cases where we may want to recall API to get session data again perhaps
    if (!props.adyenV2SessionData) {
      props.getSessionDataForAdyenV2();
    }
  }, []);
  
  useAdyenV2SessionLoader({
    locale: props.locale,
    adyenV2SessionData: props.adyenV2SessionData,
    adyenV2Environment: props.adyenV2Environment,
    adyenV2PaymentMethodsConfiguration: props.adyenV2PaymentMethodsConfiguration,
    enableAdyenV2Analytics: props.enableAdyenV2Analytics,
    adyenV2AllowedSubTypes: props.adyenV2AllowedSubTypes,
    adyenV2BlockedSubTypes: props.adyenV2BlockedSubTypes,
    handleAdyenV2CheckoutInitialisation,
    setAdyenV2PlaceOrderData: props.setAdyenV2PlaceOrderData,
    setPaymentOptionsLoadingStatus: props.setPaymentOptionsLoadingStatus,
    sendAdyenV2ActionResponse: props.sendAdyenV2ActionResponse,
  });
  
  useAdyenV2LoadedHandler({
    adyenV2Checkout: props.adyenV2Checkout,
    numberOfPaymentOptions: props.numberOfPaymentOptions,
    adyenV2OptionTypeToTest: props.adyenV2OptionTypeToTest,
    adyenV2DropinEnabled: props.adyenV2DropinEnabled,
    addAdyenV2OptionsToState: props.addAdyenV2OptionsToState,
    showStoredPaymentMethods: props.showStoredPaymentMethods,
    adyenV2CreateComponentConfig: props.adyenV2CreateComponentConfig,
    setPaymentOptionsLoadingStatus: props.setPaymentOptionsLoadingStatus,
    adyenV2SubTypesRequiringGenericBillingAddress: props.adyenV2SubTypesRequiringGenericBillingAddress,
    initialiseBillingAddressForPaymentType: props.initialiseBillingAddressForPaymentType,
  });
  
  return null;
}

AdyenV2OptionsLoader.propTypes = {
  locale: PropTypes.string.isRequired,
  numberOfPaymentOptions: PropTypes.number.isRequired,
  adyenV2Environment: PropTypes.string.isRequired,
  adyenV2SessionData: PropTypes.shape({
    clientKey: PropTypes.string.isRequired,
    sessionId: PropTypes.string.isRequired,
    sessionData: PropTypes.string.isRequired,
  }).isRequired,
  adyenV2Checkout: PropTypes.object,
  adyenV2PaymentMethodsConfiguration: PropTypes.object,
  enableAdyenV2Analytics: PropTypes.bool,
  adyenV2AllowedSubTypes: PropTypes.arrayOf(PropTypes.string),
  adyenV2BlockedSubTypes: PropTypes.arrayOf(PropTypes.string),
  adyenV2OptionTypeToTest: PropTypes.string,
  setAdyenV2PlaceOrderData: PropTypes.func,
  sendAdyenV2ActionResponse: PropTypes.func,
  displayAllAdyenV2Options: PropTypes.bool,
  adyenV2DropinEnabled: PropTypes.bool,
  getSessionDataForAdyenV2: PropTypes.func.isRequired,
  addAdyenV2OptionsToState: PropTypes.func.isRequired,
  showStoredPaymentMethods: PropTypes.bool.isRequired,
  adyenV2CreateComponentConfig: PropTypes.object.isRequired,
  setAdyenV2Checkout: PropTypes.func.isRequired,
  setPaymentOptionsLoadingStatus: PropTypes.func.isRequired,
  adyenV2SubTypesRequiringGenericBillingAddress: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialiseBillingAddressForPaymentType: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const settings = getSettings(state);
  return {
    locale: getLocaleFromSiteDataV2(state),
    numberOfPaymentOptions: getPaymentOptions(state).length,
    adyenV2SessionData: getAdyenV2SessionData(state),
    adyenV2Environment: settings?.adyen_v2_environment,
    adyenV2Checkout: getAdyenV2Checkout(state),
    adyenV2OptionTypeToTest: settings?.adyen_v2_option_type_to_test,
    adyenV2DropinEnabled: settings?.adyen_v2_dropin_enabled,
    adyenV2PaymentMethodsConfiguration: getAdyenV2PaymentMethodsConfiguration(state),
    enableAdyenV2Analytics: !!settings?.adyen_v2_analytics_enabled,
    adyenV2AllowedSubTypes: settings?.adyen_v2_allowed_sub_types,
    adyenV2BlockedSubTypes: getBlockedSubTypesForAdyenV2(state),
    showStoredPaymentMethods: !!settings?.show_stored_payment_methods_for_adyen_v2,
    adyenV2CreateComponentConfig: settings?.adyen_v2_create_component_config,
    adyenV2SubTypesRequiringGenericBillingAddress: settings?.adyen_v2_sub_types_requiring_generic_billing_address,
  };
};

const mapDispatchToProps = {
  getSessionDataForAdyenV2,
  setAdyenV2Checkout,
  addAdyenV2OptionsToState,
  setAdyenV2PlaceOrderData,
  sendAdyenV2ActionResponse,
  initialiseBillingAddressForPaymentType,
  setPaymentOptionsLoadingStatus,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdyenV2OptionsLoader);
